import { PlaceResult, AddressComponent } from 'app/atoms/GooglePlacesAutocomplete';
import { AddressOutput } from './types';

/**
 * Converts an AddressOutput (from internal API) to PlaceResult format (for Google Places compatibility)
 */
export function convertAddressOutputToPlaceResult(addressOutput: AddressOutput): PlaceResult {
  const addressComponents: AddressComponent[] = [];

  // Build address components from AddressOutput
  if (addressOutput.cityName) {
    addressComponents.push({
      long_name: addressOutput.cityName,
      short_name: addressOutput.cityName,
      types: ['locality'],
    });
  }

  if (addressOutput.state) {
    addressComponents.push({
      long_name: addressOutput.state,
      short_name: addressOutput.state,
      types: ['administrative_area_level_1'],
    });
  }

  if (addressOutput.country) {
    addressComponents.push({
      long_name: addressOutput.country,
      short_name: addressOutput.countryCode || addressOutput.country,
      types: ['country'],
    });
  }

  if (addressOutput.district) {
    addressComponents.push({
      long_name: addressOutput.district,
      short_name: addressOutput.district,
      types: ['sublocality'],
    });
  }

  if (addressOutput.zipCode) {
    addressComponents.push({
      long_name: addressOutput.zipCode,
      short_name: addressOutput.zipCode,
      types: ['postal_code'],
    });
  }

  return {
    address_components: addressComponents,
    formatted_address: addressOutput.formattedAddress || addressOutput.name || '',
    geometry: {
      location: {
        lat: () => addressOutput.latitude || 0,
        lng: () => addressOutput.longitude || 0,
      },
    },
    place_id: addressOutput.googlePlaceId || '',
    html_attributions: [],
    name: addressOutput.name || '',
  };
}

export function extractAddressInfoFromGooglePlace(apiResponse: PlaceResult): AddressOutput {
  const result: AddressOutput = {
    name: apiResponse.name || null,
    cityId: 0,
    cityName: '',
    state: null,
    country: null,
    countryCode: null,
    latitude: apiResponse.geometry.location.lat(),
    longitude: apiResponse.geometry.location.lng(),
    googlePlaceId: apiResponse.place_id,
    placeType: null,
    formattedAddress: apiResponse.formatted_address,
    district: null,
    zipCode: null,

    street: null,
    number: null,
    complement: null,
  };

  for (const component of apiResponse.address_components) {
    const types = component.types;

    if (types.includes('locality') && !result.cityName) {
      result.cityName = component.long_name;
    }

    if (types.includes('administrative_area_level_1') && !result.state) {
      result.state = component.long_name;
    }

    if (types.includes('country') && !result.country) {
      result.country = component.long_name;
      result.countryCode = component.short_name;
    }

    if (
      (types.includes('sublocality_level_1') ||
        types.includes('sublocality') ||
        types.includes('administrative_area_level_3') ||
        types.includes('locality')) &&
      !result.district
    ) {
      result.district = component.long_name;
    }

    if (types.includes('postal_code') && !result.zipCode) {
      result.zipCode = component.long_name;
    }
  }

  return result;
}
